import { useEffect, useState } from "react";
import { useGlobalContext } from "@/components/contextProvider/ContextProvider";
import OpenEnrollmentDao from "@/dao/OpenEnrollmentDao";
import type { ActionControl, DistrictWindow } from "@/types/GlobalState";

interface OpenEnrollmentData {
    loading: boolean;
    error: Error | null;
}

type DaoResponse<T> = { data: { payload: T } } | null;

type OeSchoolYearKeyAction = {
    type: "OeSchoolYearKey";
    oeSchoolYearKey: string;
};

type OeDistrictWindowsAction = {
    type: "OeDistrictWindows";
    oeDistrictWindows: DistrictWindow[];
};

type OeActionControlsAction = {
    type: "OeActionControls";
    oeActionControls: ActionControl[];
};

// New helper functions to reduce complexity of fetchData
const fetchSchoolYearKey = async (needsSchoolYear: boolean, signal: AbortSignal): Promise<string | undefined> => {
    if (needsSchoolYear) {
        const schoolYearOptions = { action: "schoolYearRead", signal };
        const schoolYearResponse = await OpenEnrollmentDao<{ payload: string }>(schoolYearOptions);
        if (schoolYearResponse?.data) {
            return schoolYearResponse.data.payload;
        }
    }
    return undefined;
};

const fetchDistrictWindowsAndActionControls = async (
    schoolYearKey: string,
    needsDistrictWindows: boolean,
    needsActionControls: boolean,
    signal: AbortSignal
): Promise<[DaoResponse<DistrictWindow[]>, DaoResponse<ActionControl[]>]> => {
    return Promise.all([
        needsDistrictWindows
            ? OpenEnrollmentDao<{ payload: DistrictWindow[] }>({
                  action: "allDistrictWindowsRead",
                  schoolYearKey,
                  signal
              })
            : Promise.resolve(null),
        needsActionControls
            ? OpenEnrollmentDao<{ payload: ActionControl[] }>({
                  action: "allActionControlsRead",
                  params: { schoolYearKey },
                  signal
              })
            : Promise.resolve(null)
    ]);
};

const dispatchOpenEnrollmentData = (
    fetchedSchoolYearKey: string | undefined,
    districtWindowsResponse: DaoResponse<DistrictWindow[]>,
    actionControlsResponse: DaoResponse<ActionControl[]>,
    dispatch: React.Dispatch<OeSchoolYearKeyAction | OeDistrictWindowsAction | OeActionControlsAction>
): void => {
    if (fetchedSchoolYearKey) {
        dispatch({ type: "OeSchoolYearKey", oeSchoolYearKey: fetchedSchoolYearKey });
    }

    if (districtWindowsResponse?.data) {
        const { payload } = districtWindowsResponse.data;
        dispatch({ type: "OeDistrictWindows", oeDistrictWindows: payload });
    }

    if (actionControlsResponse?.data) {
        const { payload } = actionControlsResponse.data;
        dispatch({ type: "OeActionControls", oeActionControls: payload });
    }
};

export const useOpenEnrollmentData = (): OpenEnrollmentData => {
    const { dispatch, state } = useGlobalContext();
    const { oeActionControls, oeDistrictWindows, oeSchoolYearKey, token } = state || {};
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return;
        }

        const needsSchoolYear = !oeSchoolYearKey;
        const needsDistrictWindows = !oeDistrictWindows;
        const needsActionControls = !oeActionControls;

        // If no data is needed, exit early.
        if (!needsSchoolYear && !needsDistrictWindows && !needsActionControls) {
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        const { signal } = controller;

        const fetchData = async (): Promise<void> => {
            try {
                setLoading(true);
                const fetchedSchoolYearKey = await fetchSchoolYearKey(needsSchoolYear, signal);
                const schoolYearKey = oeSchoolYearKey || fetchedSchoolYearKey;

                if (!schoolYearKey) {
                    throw new Error("Failed to fetch school year key.");
                }

                const [districtWindowsResponse, actionControlsResponse] = await fetchDistrictWindowsAndActionControls(
                    schoolYearKey,
                    needsDistrictWindows,
                    needsActionControls,
                    signal
                );

                // 3. Now that all fetching is complete, dispatch all updates.
                if (signal.aborted) return;
                dispatchOpenEnrollmentData(
                    fetchedSchoolYearKey,
                    districtWindowsResponse,
                    actionControlsResponse,
                    dispatch
                );
            } catch (err) {
                if ((err as Error).name !== "AbortError") {
                    console.error("Failed to fetch Open Enrollment data:", err);
                    setError(err as Error);
                }
            } finally {
                setLoading(false);
            }
        };

        void fetchData();

        return (): void => controller.abort();
    }, [token, dispatch, oeSchoolYearKey, oeDistrictWindows, oeActionControls]);

    return { loading, error };
};
